import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { SocialLink, SocialPlatform } from "../../types/outreach";
import {
  SOCIAL_PLATFORM_METADATA,
  SOCIAL_PLATFORM_ORDER,
  normalizeSocialLinkInput,
  resolveSocialLinks,
  sortSocialLinks,
} from "../../utils/socialLinks";
import { IoLogoInstagram, IoLogoTwitter, IoLogoYoutube } from "react-icons/io";
import { SiTiktok, SiFacebook } from "react-icons/si";
import LinkIcon from "@mui/icons-material/Link";
import LanguageIcon from "@mui/icons-material/Language";

type SocialLinksInputProps = {
  value?: SocialLink[] | null;
  legacyValue?: string | null;
  onChange: (links: SocialLink[]) => void;
  helperText?: React.ReactNode;
  label?: string;
  disabled?: boolean;
};

const platformIcons: Partial<Record<SocialPlatform, React.ReactElement>> = {
  instagram: <IoLogoInstagram />,
  twitter: <IoLogoTwitter />,
  youtube: <IoLogoYoutube />,
  tiktok: <SiTiktok />,
  facebook: <SiFacebook />,
  website: <LanguageIcon fontSize="small" />,
  other: <LinkIcon fontSize="small" />,
};

const getInitialPlatform = (links: SocialLink[]): SocialPlatform => {
  if (links.length > 0) {
    return links[0].platform;
  }
  return "instagram";
};

export const SocialLinksInput: React.FC<SocialLinksInputProps> = ({
  value,
  legacyValue,
  onChange,
  helperText,
  label = "Social Profiles",
  disabled = false,
}) => {
  const resolvedLinks = useMemo(
    () => resolveSocialLinks(value ?? undefined, legacyValue ?? undefined),
    [legacyValue, value]
  );
  const [platform, setPlatform] = useState<SocialPlatform>(
    getInitialPlatform(resolvedLinks)
  );
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (resolvedLinks.length === 0 && platform !== "instagram") {
      setPlatform("instagram");
      return;
    }

    if (
      resolvedLinks.length > 0 &&
      !resolvedLinks.some((link) => link.platform === platform)
    ) {
      setPlatform(resolvedLinks[0].platform);
    }
  }, [platform, resolvedLinks]);

  const handlePlatformChange = (event: SelectChangeEvent<SocialPlatform>) => {
    setPlatform(event.target.value as SocialPlatform);
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setInputValue(event.target.value);
    setError(null);
  };

  const handleAdd = () => {
    if (!inputValue.trim()) {
      setError("Enter a handle or paste a profile URL");
      return;
    }

    try {
      const normalized = normalizeSocialLinkInput(platform, inputValue);
      const current = sortSocialLinks(resolvedLinks);
      const filtered = current.filter(
        (link) =>
          !(
            link.platform === normalized.platform &&
            link.handle.toLowerCase() === normalized.handle.toLowerCase()
          )
      );
      const nextLinks = sortSocialLinks([...filtered, normalized]);
      onChange(nextLinks);
      setInputValue("");
      setError(null);
    } catch {
      setError("Unable to recognize that handle. Try a full URL instead.");
    }
  };

  const handleRemove = (index: number) => {
    const nextLinks = resolvedLinks.filter((_, i) => i !== index);
    onChange(nextLinks);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAdd();
    }
  };

  const displayedLinks = sortSocialLinks(resolvedLinks);

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <FormControl sx={{ minWidth: 160 }} size="small" disabled={disabled}>
          <InputLabel id="social-platform-label">Platform</InputLabel>
          <Select
            labelId="social-platform-label"
            label="Platform"
            value={platform}
            onChange={handlePlatformChange}
          >
            {SOCIAL_PLATFORM_ORDER.map((platformKey) => (
              <MenuItem key={platformKey} value={platformKey}>
                {SOCIAL_PLATFORM_METADATA[platformKey]?.label ?? platformKey}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Handle or URL"
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={disabled}
          fullWidth
          helperText={error || helperText}
          error={Boolean(error)}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={disabled}
          sx={{
            alignSelf: { xs: "stretch", sm: "center" },
            minWidth: 120,
            marginTop: "-45px !important",
          }}
        >
          Add
        </Button>
      </Stack>
      {displayedLinks.length > 0 ? (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {displayedLinks.map((link, index) => (
            <Chip
              key={`${link.platform}-${link.handle}`}
              icon={
                platformIcons[link.platform] ?? <LinkIcon fontSize="small" />
              }
              label={
                <Box
                  component="span"
                  sx={{ display: "inline-flex", gap: 0.75 }}
                >
                  <Typography component="span" variant="body2">
                    {SOCIAL_PLATFORM_METADATA[link.platform]?.label ??
                      link.platform}
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {link.label ?? link.handle}
                  </Typography>
                </Box>
              }
              onDelete={disabled ? undefined : () => handleRemove(index)}
              deleteIcon={<CloseIcon fontSize="small" />}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No social profiles added yet.
        </Typography>
      )}
    </Stack>
  );
};

export default SocialLinksInput;
